import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { Box } from '@spark-web/box';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { VerticalStepper } from '@spark-web/vertical-stepper';
import ApprovedIcon from '../../../assets/application-approved.svg?react';
import UnderReviewIcon from '../../../assets/application-under-review.svg?react';
import PageTitle from '../../components/PageTitle/PageTitle';
import ApplicationUnderReviewStep from '../../components/Step/ApplicationUnderReviewStep';
import Step from '../../components/Step/Step';
import { APPLICATION_UNDER_REVIEW, SESSION_TEARDOWN_DEADLINE } from '../../utils';
import WithdrawnApplication from '../SubmittedApplication/WithdrawnApplication';
import CustomerPortalCard from './CustomerPortalCard';
import CustomerPortalCardApproved from './CustomerPortalCardApproved';
import DeclinedApplication from './DeclinedApplication';
import { useGetNextStepIndex, useGetStatusSteps } from './hooks';
import { ApplicationSubmissionStatus, StatusStep, StepType } from './types';

// Instant-approval products resolve to REFERRED rather than SUBMITTED; both count as under review.
const UNDER_REVIEW_STATUSES = new Set([ApplicationSubmissionStatus.SUBMITTED, ApplicationSubmissionStatus.REFERRED]);

const CUSTOMER_PORTAL_ELIGIBLE_STATUSES = new Set([
  ApplicationSubmissionStatus.SUBMITTED,
  ApplicationSubmissionStatus.REFERRED,
  ApplicationSubmissionStatus.APPROVED,
]);

type NextStepsProps = {
  readonly status: ApplicationSubmissionStatus;
  readonly isStatusSettled: boolean;
};

function NextSteps({ status, isStatusSettled }: NextStepsProps) {
  const steps = useGetStatusSteps(status);
  const activeStep = useGetNextStepIndex(steps, status);
  const { clearCredentials } = useAuth0();
  const flags = useFlags();
  const showCustomerPortalAction =
    (flags['redirect-customer-to-customer-portal'] ?? false) && CUSTOMER_PORTAL_ELIGIBLE_STATUSES.has(status);

  const isUnderReview = UNDER_REVIEW_STATUSES.has(status);
  const isApproved = status === ApplicationSubmissionStatus.APPROVED;

  const hasClearedSession = useRef(false);
  // clearCredentials gets a new identity every render (its useCallback closes over a fresh
  // LocalStorageCache), so it can't be an effect dependency without re-firing the effect in a
  // loop. Reading it through a ref keeps the teardown on the latest cache key without that churn.
  const clearCredentialsRef = useRef(clearCredentials);
  clearCredentialsRef.current = clearCredentials;

  const clearSessionOnce = useCallback(() => {
    if (hasClearedSession.current) {
      return;
    }
    hasClearedSession.current = true;
    clearCredentialsRef.current();
  }, []);

  // CT-3287: gated on isStatusSettled rather than firing on mount. Clearing the session empties
  // the token cache that the status query resolves lazily at request time, so an unconditional
  // mount-time clear sends that request out unauthenticated and shows a spurious "session expired"
  // dialog. For instant-approval products this defers the clear until the polling timer is done.
  useEffect(() => {
    if (isStatusSettled) {
      clearSessionOnce();
    }
  }, [isStatusSettled, clearSessionOnce]);

  // CT-3287 backstop: the status query can stay unsettled indefinitely — a hung request keeps
  // `isFetching` true, as does react-query's retry backoff — and the teardown above would then
  // never run, leaving credentials in local storage. Tear down unconditionally once the deadline
  // passes; it sits well past the polling window, so it only fires for a genuinely stuck request.
  useEffect(() => {
    const deadlineTimeout = setTimeout(clearSessionOnce, SESSION_TEARDOWN_DEADLINE * 1000);

    return () => clearTimeout(deadlineTimeout);
  }, [clearSessionOnce]);

  if (status === ApplicationSubmissionStatus.WITHDRAWN) {
    return <WithdrawnApplication />;
  }

  if (status === ApplicationSubmissionStatus.DECLINED) {
    return <DeclinedApplication />;
  }
  return (
    <Stack gap={isUnderReview ? 'xxxlarge' : 'xxlarge'}>
      <Stack gap={{ mobile: 'xlarge', tablet: 'xxlarge' }}>
        <Stack gap={{ mobile: 'large', tablet: 'xlarge' }}>
          {(isUnderReview || isApproved) && (
            <Box display="flex" justifyContent="center">
              {isUnderReview ? <UnderReviewIcon /> : <ApprovedIcon />}
            </Box>
          )}
          <PageTitle status={status} />
        </Stack>
        {isUnderReview && (
          <Text align="center" size="large">
            {APPLICATION_UNDER_REVIEW.PAGE_DESCRIPTION}
          </Text>
        )}
      </Stack>
      <VerticalStepper activeIndex={activeStep}>
        {steps.map((step: StepType) =>
          step.key === StatusStep.APPLICATION_UNDER_REVIEW ? (
            <ApplicationUnderReviewStep
              key={step.key}
              customerPortalCard={
                showCustomerPortalAction ? <CustomerPortalCard applicationStatus={status} /> : undefined
              }
            />
          ) : (
            <Step {...step} key={step.key} />
          ),
        )}
      </VerticalStepper>
      {/* Approved has no "Application under review" step to nest the banner under, so it sits below the stepper. */}
      {showCustomerPortalAction && !isUnderReview && <CustomerPortalCardApproved applicationStatus={status} />}
    </Stack>
  );
}

export default NextSteps;
